import { useState } from 'react';

const services = [
  {
    icon: 'ri-flow-chart',
    title: 'Workflow Automation',
    description: 'n8n / Low-Code automation that connects your entire tech stack seamlessly.',
    gradient: 'from-purple-500 to-pink-500',
  },
  {
    icon: 'ri-links-line',
    title: 'API-Based System Integrations',
    description: 'Connect disparate systems with robust, scalable API integrations.',
    gradient: 'from-cyan-500 to-blue-500',
  },
  {
    icon: 'ri-robot-2-line',
    title: 'AI Agent Development',
    description: 'CrewAI / LangGraph powered intelligent agents that work autonomously.',
    gradient: 'from-violet-500 to-purple-500',
  },
  {
    icon: 'ri-code-s-slash-line',
    title: 'RPA-Lite Python Bots',
    description: 'Lightweight automation bots that handle repetitive tasks efficiently.',
    gradient: 'from-orange-500 to-red-500',
  },
  {
    icon: 'ri-database-2-line',
    title: 'Enterprise Data Pipelines',
    description: 'Scalable data pipelines that transform raw data into actionable insights.',
    gradient: 'from-green-500 to-emerald-500',
  },
  {
    icon: 'ri-brain-line',
    title: 'Custom LLM Integrations',
    description: 'Integrate cutting-edge language models into your business workflows.',
    gradient: 'from-pink-500 to-rose-500',
  },
];

export default function ServicesSection() {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  return (
    <section className="relative py-32 px-6 bg-gradient-to-b from-[#0a0a0f] to-[#1a0b2e]">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle, rgba(139, 92, 246, 0.3) 1px, transparent 1px)`,
            backgroundSize: '30px 30px',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="text-5xl md:text-6xl font-black mb-6">
            <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
              Our Services
            </span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            End-to-end automation solutions that transform your business operations
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="group relative"
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {/* Card */}
              <div
                className="relative h-full p-8 rounded-2xl border border-purple-500/20 bg-gradient-to-br from-purple-900/10 to-transparent backdrop-blur-sm transition-all duration-500"
                style={{
                  transform: hoveredIndex === index ? 'translateY(-8px)' : 'translateY(0)',
                  boxShadow:
                    hoveredIndex === index
                      ? '0 20px 60px rgba(139, 92, 246, 0.3)'
                      : '0 4px 20px rgba(0, 0, 0, 0.3)',
                }}
              >
                {/* Gradient Line Separator */}
                <div
                  className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${service.gradient} rounded-t-2xl transition-opacity duration-300`}
                  style={{ opacity: hoveredIndex === index ? 1 : 0.3 }}
                />

                {/* Glow Effect */}
                {hoveredIndex === index && (
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-10 rounded-2xl blur-xl transition-opacity duration-500`}
                  />
                )}

                {/* Icon */}
                <div className="relative mb-6">
                  <div
                    className={`w-16 h-16 flex items-center justify-center rounded-xl bg-gradient-to-br ${service.gradient} transition-transform duration-500`}
                    style={{
                      transform: hoveredIndex === index ? 'scale(1.1) rotate(5deg)' : 'scale(1)',
                    }}
                  >
                    <i className={`${service.icon} text-3xl text-white`} />
                  </div>

                  {/* Floating Animation */}
                  {hoveredIndex === index && (
                    <div className="absolute -top-2 -right-2 w-4 h-4">
                      <div
                        className={`w-full h-full rounded-full bg-gradient-to-br ${service.gradient}`}
                        style={{ animation: 'ping 1s cubic-bezier(0, 0, 0.2, 1) infinite' }}
                      />
                    </div>
                  )}
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold mb-4 text-white group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-purple-400 group-hover:to-cyan-400 group-hover:bg-clip-text transition-all duration-300">
                  {service.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">{service.description}</p>
              </div>

              {/* Particle Effect */}
              {hoveredIndex === index && (
                <>
                  {[...Array(6)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute w-1 h-1 bg-purple-400 rounded-full pointer-events-none"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                        animation: `particleFloat ${1 + Math.random()}s ease-out forwards`,
                      }}
                    />
                  ))}
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes particleFloat {
          0% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
          100% {
            opacity: 0;
            transform: translateY(-50px) scale(0);
          }
        }
      `}</style>
    </section>
  );
}
